import { validateEmail } from '@disco/utils/validation'

import React, { useState } from 'react'
import { Box, Flex, Heading, Link, Text, TextField } from 'components/ds'
import Button from 'components/ui/button'
import ButtonContainer from 'components/ui/button-container'
import Label from '../label'
import Warning from '../warning'
type Props = {
  onSubmit: (email: string) => Promise<{
    success: boolean
  }>
  onDismiss?: () => void
}

/**
 * Forgot password form component with email validation and submission
 * Provides a form to reset password with success/error states and navigation
 * @param onSubmit - Async function to submit email for password reset
 * @param onDismiss - Optional callback to dismiss the form and return to sign in
 */
const ForgotPasswordForm = ({
  onSubmit,
  onDismiss,
}: Props): React.ReactNode => {
  const [email, setEmail] = useState<string>('')
  const [success, setSuccess] = useState<boolean>(false)
  const [error, setError] = useState<React.ReactNode | null>(null)
  const [submitting, setSubmitting] = useState(false)
  const valid = validateEmail(email)

  const handleSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault()
    if (!valid) return
    setSubmitting(true)

    try {
      await onSubmit(email.trim())
      setSuccess(true)
    } catch (error) {
      setError('There was an error sending the reset password email')
    }

    setSubmitting(false)
  }

  return (
    <Flex asChild direction="column" gap="3">
      <form onSubmit={handleSubmit}>
        <Heading align="center" mt="0">
          Reset Password
        </Heading>

        {!success ? (
          <>
            <Box>
              <Label htmlFor="email" caption="Email" />
              <TextField.Root
                size="3"
                id="email"
                type="text"
                value={email}
                onChange={event => {
                  setEmail(event.target.value)
                  setError(null)
                }}
                placeholder="you@company.com"
                autoComplete="email"
                autoCapitalize="off"
                disabled={submitting} // eslint-disable-next-line jsx-a11y/no-autofocus
                autoFocus
              />
            </Box>

            <ButtonContainer vertical>
              <Button
                type="submit"
                appearance="primary"
                large
                caption="Send me instructions"
                fullWidth
                textCenter
                progressIndicator={submitting}
                progressCaption="Sending"
              />
              {error && <Warning>{error}</Warning>}
              {onDismiss && (
                <Flex direction="column" gap="2" mt="2">
                  <Text align="center" size="2">
                    <Link
                      href="#"
                      onClick={e => {
                        e.preventDefault()
                        onDismiss()
                      }}
                    >
                      Back to Sign in
                    </Link>
                  </Text>
                </Flex>
              )}
            </ButtonContainer>
          </>
        ) : (
          <Text>
            Email has been sent to {email} with instructions on resetting your
            password.
          </Text>
        )}
      </form>
    </Flex>
  )
}

export default ForgotPasswordForm
